import { AttachmentCategory, type AttachmentSelectDto, AttachmentType } from '@@types/api.types';
import { FILE_EXTENSION_BY_CONTENT_TYPE } from '@azymmetric/shared/attachments/attachment.constants';
import type { BufferedAttachment } from '@queries/attachments.query';
import { hasFileExtension } from '@utils/attachment-download.util';
import { FileIcon, ImageIcon, LinkIcon } from 'lucide-react';
import {
  TECHNOLOGY_ATTACHMENT_EDITOR_IMAGE_KIND,
  type TechnologyAttachmentEditorKind,
  getTechnologyAttachmentEditorKind,
  isTechnologyAttachmentImageKind,
} from './technology-attachments.util';

export const ATTACHMENT_TYPE_OPTIONS: {
  icon: typeof FileIcon;
  label: string;
  value: TechnologyAttachmentEditorKind;
}[] = [
  { icon: FileIcon, label: 'File', value: AttachmentType.FILE },
  { icon: LinkIcon, label: 'URL', value: AttachmentType.URL },
  { icon: ImageIcon, label: 'Image', value: TECHNOLOGY_ATTACHMENT_EDITOR_IMAGE_KIND },
];

export const getAttachmentCategory = (params: {
  category: AttachmentCategory;
  kind: TechnologyAttachmentEditorKind;
}): AttachmentCategory => {
  const { category, kind } = params;
  return isTechnologyAttachmentImageKind(kind) ? AttachmentCategory.TECHNOLOGY_IMAGE : category;
};

export const getAttachmentTypeForKind = (kind: TechnologyAttachmentEditorKind): AttachmentType => {
  return kind === AttachmentType.URL ? AttachmentType.URL : AttachmentType.FILE;
};

export const getDefaultCreateKind = (params: {
  allowTechnologyImages: boolean;
  category: AttachmentCategory;
  defaultType: AttachmentType;
}): TechnologyAttachmentEditorKind => {
  const { allowTechnologyImages, category, defaultType } = params;

  if (allowTechnologyImages && category === AttachmentCategory.TECHNOLOGY_IMAGE) {
    return TECHNOLOGY_ATTACHMENT_EDITOR_IMAGE_KIND;
  }

  return defaultType;
};

export const getEditorKind = (params: {
  allowTechnologyImages: boolean;
  attachmentToEdit: AttachmentSelectDto | null;
  bufferedAttachmentToEdit?: BufferedAttachment | null;
  category: AttachmentCategory;
  defaultType: AttachmentType;
}): TechnologyAttachmentEditorKind => {
  const { allowTechnologyImages, attachmentToEdit, bufferedAttachmentToEdit, category, defaultType } = params;

  if (attachmentToEdit) {
    return allowTechnologyImages ? getTechnologyAttachmentEditorKind(attachmentToEdit) : attachmentToEdit.type;
  }

  if (bufferedAttachmentToEdit) {
    if (allowTechnologyImages && bufferedAttachmentToEdit.category === AttachmentCategory.TECHNOLOGY_IMAGE) {
      return TECHNOLOGY_ATTACHMENT_EDITOR_IMAGE_KIND;
    }

    return bufferedAttachmentToEdit.type;
  }

  return getDefaultCreateKind({ allowTechnologyImages, category, defaultType });
};

export const getInitialFeaturedValue = (params: {
  attachmentToEdit: AttachmentSelectDto | null;
  bufferedAttachmentToEdit?: BufferedAttachment | null;
  editorKind: TechnologyAttachmentEditorKind;
  isEdit: boolean;
}): boolean => {
  const { attachmentToEdit, bufferedAttachmentToEdit, editorKind, isEdit } = params;

  if (!isTechnologyAttachmentImageKind(editorKind)) {
    return false;
  }

  if (isEdit) {
    return Boolean(attachmentToEdit?.featured ?? bufferedAttachmentToEdit?.featured);
  }

  return false;
};

export const getSubmitButtonLabel = (params: {
  isEdit: boolean;
  kind: TechnologyAttachmentEditorKind;
}): string => {
  const { isEdit, kind } = params;

  if (isEdit) {
    return 'Update';
  }

  if (isTechnologyAttachmentImageKind(kind)) {
    return 'Upload Image';
  }

  if (kind === AttachmentType.URL) {
    return 'Save Link';
  }

  return 'Upload File';
};

export const getUpdateErrorMessage = (isImage: boolean): string => {
  return isImage ? 'Failed to update technology image.' : 'Failed to save attachment.';
};

export const resolveAttachmentFileName = (params: {
  attachmentToEdit: AttachmentSelectDto | null;
  bufferedAttachmentToEdit?: BufferedAttachment | null;
}): string => {
  const { attachmentToEdit, bufferedAttachmentToEdit } = params;

  if (bufferedAttachmentToEdit?.file?.name) {
    return bufferedAttachmentToEdit.file.name;
  }

  if (!attachmentToEdit) {
    return '-';
  }

  const safeTitle = attachmentToEdit.title.trim() || 'attachment';
  if (hasFileExtension(safeTitle)) {
    return safeTitle;
  }

  const normalizedContentType = attachmentToEdit.contentType?.split(';')[0]?.trim().toLowerCase();
  if (!normalizedContentType) {
    return safeTitle;
  }

  const fileExtension = FILE_EXTENSION_BY_CONTENT_TYPE[normalizedContentType];
  return fileExtension ? `${safeTitle}.${fileExtension}` : safeTitle;
};
